import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { AutoComplete } from 'primereact/autocomplete';
import type { AutoCompleteProps } from 'primereact/autocomplete';
import { useHistory } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { ContextMenu } from 'primereact/contextmenu';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { FileText, MapPin, Phone, ShoppingCart, User } from 'lucide-react';

import { checkPermission, redirectPage } from '../../../../utils/Helpers';
import permissions from '../../../../config/permissions';
import {
  CLIENT_DETAILS_UPDATED_EVENT,
  isNoCodeLowCodeModuleEnabled,
} from '../../../../constants';
import { citiesData } from '../../../../constants/CitiesListData';
import {
  fetchGlobalFilterSectionOptions,
  searchFormModuleReportData,
} from '../../../Admin/NewConfiguration/service/global-filter-section.service';
import { GlobalSearchDropdownOption } from '../../../Admin/NewConfiguration/GlobalSearchBar/globalSearchBar.interface';
import {
  FILTER_SECTION_CATALOG_UPDATED_EVENT,
  FILTER_SECTION_HEADER_OPTION_META,
  getCachedFilterSectionSearchOptions,
  getEntityViewPath,
  isFilterSectionOptionValue,
  mapReportRowToSearchItem,
  parseFilterSectionOptionValue,
  resolveFilterSectionSearchOptions,
} from '../../../Admin/NewConfiguration/GlobalSearchBar/helpers/global-filter-section.helper';
import { validateGlobalSearchInput } from '../helpers/validation.helper';
import {
  GlobalProjectSearchParams,
  GlobalSearchOptions,
  OptionType,
  ProjectSearchProps,
  SearchValueType,
} from '../interface/app-header';
import { searchProjects } from '../services/header.service';
import styles from '../css/navbar.module.css';
import {
  FORCE_SUGGESTION_KEYS,
  NO_DATA_FOUND,
  PROJECT_AND_ADDRESS_KEYS,
  PROJECT_KEYS,
  SUGGESTION_ENABLED_KEYS,
  URL_REG_EXP,
} from '../constants/menu-items.constants';
import { GLOBAL_SEARCH_PARAMETERS_OPTIONS_WITH_ICON } from '../constants/header.constant';

const readTenantSearchConfig = () => {
  try {
    const details = JSON.parse(localStorage.getItem('client_details') || '{}');
    return {
      search_options: Array.isArray(details?.search_options)
        ? details.search_options
        : [],
      default_search_option: details?.default_search_option,
      clear_global_search_text: details?.clear_global_search_text,
      no_code_low_code_module: details?.no_code_low_code_module,
    };
  } catch {
    return {
      search_options: [],
      default_search_option: undefined,
      clear_global_search_text: undefined,
      no_code_low_code_module: undefined,
    };
  }
};

const buildSearchTypeOptions = (
  catalog: GlobalSearchDropdownOption[] = [],
  nclcEnabled = false
): GlobalSearchOptions[] => [
  ...GLOBAL_SEARCH_PARAMETERS_OPTIONS_WITH_ICON,
  ...(nclcEnabled
    ? catalog
        .filter(
          option => Boolean(option?.label?.trim()) && Boolean(option?.value)
        )
        .map(option => ({
          ...option,
          ...FILTER_SECTION_HEADER_OPTION_META,
        }))
    : []),
];

const ProjectSearchBar: React.FC<ProjectSearchProps> = ({ setVisible }) => {
  const toast = useRef<Toast>(null);
  const history = useHistory();
  const cm = useRef<ContextMenu | null>(null);
  const autoRef = useRef<AutoComplete | null>(null);

  const currentURL = window.location.href;

  const match = currentURL.match(URL_REG_EXP);
  const [searchParameter, setSearchParameter] =
    useState<string>('project_number');
  const [tenantSearchConfig, setTenantSearchConfig] = useState(
    readTenantSearchConfig
  );
  const isNclcEnabled = isNoCodeLowCodeModuleEnabled({
    no_code_low_code_module: tenantSearchConfig.no_code_low_code_module,
  });
  const [searchTypeOptions, setSearchTypeOptions] = useState<
    GlobalSearchOptions[]
  >(() => {
    const config = readTenantSearchConfig();
    const nclcEnabled = isNoCodeLowCodeModuleEnabled({
      no_code_low_code_module: config.no_code_low_code_module,
    });
    return buildSearchTypeOptions(
      nclcEnabled ? getCachedFilterSectionSearchOptions() : [],
      nclcEnabled
    );
  });

  const [options, setOptions] = useState<OptionType[]>([]);
  const [searchSuggession, setSearchSuggession] = useState<number>(
    parseInt(localStorage.getItem('search-suggession') ?? '1')
  );

  const [selectedCityName, setSelectedCityName] = useState<string | null>(null);
  const [addressSearch, setAddressSearch] = useState<string>('');
  const [value, setValue] = useState<string>('');
  const [searchLength, setSearchLength] = useState<number>(0);
  const [isHardRefresh, setIsHardRefresh] = useState<boolean | number>(false);
  const [globalSearchLoader, setGlobalSearchLoader] = useState<
    boolean | number
  >(false);

  const clearToast = () => {
    toast.current?.clear();
  };
  useEffect(() => {
    if (options.length === 0 && searchLength >= 3) {
      clearToast();
    }
  }, [options]);

  useEffect(() => {
    if (match) {
      const searchType = match[1];
      const searchValue = match[2];
      const decodedAddress = decodeURIComponent(
        searchValue.replace(/%20/g, ' ')
      );

      if (currentURL) {
        setValue(decodedAddress);
        setSearchParameter(
          !isNclcEnabled && isFilterSectionOptionValue(searchType)
            ? 'project_number'
            : searchType
        );
      } else {
        setValue('');
        setSearchParameter('project_number');
      }
    }
  }, [currentURL, isNclcEnabled]);

  useEffect(() => {
    const defaultOption = tenantSearchConfig.default_search_option;
    if (
      tenantSearchConfig.search_options?.length > 0 &&
      defaultOption &&
      (isNclcEnabled || !isFilterSectionOptionValue(String(defaultOption)))
    ) {
      setSearchParameter(defaultOption);
      return;
    }

    if (
      !isNclcEnabled &&
      isFilterSectionOptionValue(String(defaultOption || searchParameter))
    ) {
      setSearchParameter('project_number');
    }
  }, [
    isNclcEnabled,
    tenantSearchConfig.default_search_option,
    tenantSearchConfig.search_options,
  ]);

  useEffect(() => {
    const handleClientDetailsUpdated = () => {
      setTenantSearchConfig(readTenantSearchConfig());
    };
    const handleCatalogUpdated = () => {
      const config = readTenantSearchConfig();
      const nclcEnabled = isNoCodeLowCodeModuleEnabled({
        no_code_low_code_module: config.no_code_low_code_module,
      });
      setSearchTypeOptions(
        buildSearchTypeOptions(
          nclcEnabled ? getCachedFilterSectionSearchOptions() : [],
          nclcEnabled
        )
      );
    };

    window.addEventListener(
      CLIENT_DETAILS_UPDATED_EVENT,
      handleClientDetailsUpdated
    );
    window.addEventListener(
      FILTER_SECTION_CATALOG_UPDATED_EVENT,
      handleCatalogUpdated
    );

    return () => {
      window.removeEventListener(
        CLIENT_DETAILS_UPDATED_EVENT,
        handleClientDetailsUpdated
      );
      window.removeEventListener(
        FILTER_SECTION_CATALOG_UPDATED_EVENT,
        handleCatalogUpdated
      );
    };
  }, []);

  const selectedSearchOptionsKey = (
    tenantSearchConfig.search_options || []
  ).join(',');

  useEffect(() => {
    if (!isNclcEnabled) {
      setSearchTypeOptions(buildSearchTypeOptions([], false));
      return;
    }

    let cancelled = false;

    const loadCatalogSearchOptions = async () => {
      const saved = await fetchGlobalFilterSectionOptions();
      if (cancelled) {
        return;
      }

      const resolved = resolveFilterSectionSearchOptions(saved);
      setSearchTypeOptions(
        buildSearchTypeOptions(
          resolved.length ? resolved : getCachedFilterSectionSearchOptions(),
          true
        )
      );
    };

    void loadCatalogSearchOptions();

    return () => {
      cancelled = true;
    };
  }, [isNclcEnabled, selectedSearchOptionsKey]);

  const onSearchSuggessionHandler = (param: boolean) => {
    setSearchSuggession(param ? 1 : 0);
    localStorage.setItem('search-suggession', (param ? 1 : 0).toString());
  };
  const itemTemplate: AutoCompleteProps['itemTemplate'] = item => {
    return (
      <>
        {PROJECT_KEYS.includes(searchParameter) && (
          <div className="text-xs border-top-none border-left-none border-right-none border-cyan-50 py-0">
            <div className="flex justify-content-between align-items-center">
              {item?.project_number ? (
                <p className="my-0 project_number-name pt-1 pr-2 flex align-itenx-center">
                  <FileText size="14" className="mr-1 text-blue-600" />
                  {item?.project_number}
                </p>
              ) : (
                <div className="flex align-items-center">
                  {item?.customer_name && (
                    <>
                      <User size="14" className="mr-1 text-green-400" />
                      <p className="my-0 customer_name">
                        {item?.customer_name}
                      </p>
                    </>
                  )}
                </div>
              )}

              {item?.project_type?.project_type || item?.status?.status ? (
                <div className="text-center">
                  {item?.status?.status ? (
                    item?.status?.status === 'Scheduled' ? (
                      <Tag
                        severity="success"
                        className="justify-content-center font-light"
                        value={item?.status?.status}
                        pt={{
                          value: {
                            className: 'text-10',
                          },
                        }}
                      />
                    ) : (
                      <Tag
                        severity="info"
                        className="justify-content-center font-light"
                        value={item?.status?.status}
                        pt={{
                          value: {
                            className: 'text-10',
                          },
                        }}
                      />
                    )
                  ) : null}
                </div>
              ) : null}
            </div>

            {item?.po_number ? (
              <div className="flex justify-content-between align-items-center mt-1">
                {item?.po_number && (
                  <span className="flex align-items-center">
                    <ShoppingCart size="14" className="mr-1 text-purple-400" />
                    <p className="my-0 po_number">PO# {item?.po_number}</p>
                  </span>
                )}

                <Tag
                  className="justify-content-center font-light"
                  value={item?.project_type?.project_type}
                  pt={{
                    value: {
                      className: 'text-10',
                    },
                  }}
                />
              </div>
            ) : null}
            <div className="flex align-items-center mt-1">
              {item?.customer?.first_name && (
                <p className="my-1 flex align-items-center mr-3">
                  <User size="14" className="mr-1 text-green-400" />
                  {item?.customer?.first_name} {item?.customer?.last_name}
                </p>
              )}

              {item?.created_user?.google_email_address ||
              item?.created_user?.contact_no ? (
                item?.created_user?.google_email_address ? (
                  <p className="google_email_address">
                    {item?.created_user?.google_email_address}
                  </p>
                ) : (
                  <p className="my-1 flex align-items-center">
                    <Phone size="14" className="mr-1 text-cyan-600" /> Ph:
                    {item?.created_user?.contact_no}
                  </p>
                )
              ) : null}
            </div>

            {item?.installation_address?.address1 && (
              <p className="my-2  flex align-items-center">
                <MapPin size="14" className="mr-1 text-pink-600" />
                {item?.installation_address?.address1}
              </p>
            )}
          </div>
        )}
        {/* Project and PO ends */}

        {/* Clients and phone starts */}
        {FORCE_SUGGESTION_KEYS.includes(searchParameter) && (
          <div className="text-xs border-top-none border-left-none border-right-none border-cyan-50 py-0">
            <div className="flex align-items-center">
              {item?.project_number ? (
                <p className="my-1 project_number-name pt-1 pr-2 flex align-itenx-center">
                  <FileText size="14" className="mr-1 text-blue-600" />
                  {item?.project_number}
                </p>
              ) : (
                <>
                  {item?.customer_name && (
                    <div className="my-1 flex align-items-center mr-3">
                      <User size="14" className="mr-1 text-green-400" />
                      <p className="my-0 customer_name">
                        {item?.customer_name}
                      </p>
                    </div>
                  )}
                </>
              )}
              {item?.mobile_phone && (
                <p className="my-1 flex align-items-center">
                  <Phone size="14" className="mr-1 text-cyan-600" />
                  {item?.mobile_phone}
                </p>
              )}
            </div>
            {item?.billing_address && (
              <div className="flex align-items-center">
                <MapPin size="14" className="mr-1 text-pink-600" />{' '}
                <p className="my-2  billing_address">{item?.billing_address}</p>
              </div>
            )}
          </div>
        )}
        {/* Clients and phone ends */}

        {/* Address starts */}
        {PROJECT_AND_ADDRESS_KEYS[2].includes(searchParameter) && (
          <div className="text-xs border-top-none border-left-none border-right-none border-cyan-50 py-0">
            <>
              <p className="my-0 flex align-items-center justify-content-between">
                {item?.project_number && (
                  <p className="my-0 project_number-name pt-1 pr-2 flex align-itenx-center">
                    <FileText size="14" className="mr-1 text-blue-600" />
                    {item?.project_number}
                  </p>
                )}
                {item?.project_type?.project_type || item?.status?.status ? (
                  <div className="text-center">
                    {item?.status?.status ? (
                      item?.status?.status === 'Scheduled' ? (
                        <Tag
                          severity="success"
                          className="justify-content-center font-light"
                          value={item?.status?.status}
                          pt={{
                            value: {
                              className: 'text-10',
                            },
                          }}
                        />
                      ) : (
                        <Tag
                          severity="info"
                          className="justify-content-center font-light"
                          value={item?.status?.status}
                          pt={{
                            value: {
                              className: 'text-10',
                            },
                          }}
                        />
                      )
                    ) : null}
                  </div>
                ) : null}
              </p>
              {item?.customer?.first_name && (
                <p className="my-1 flex align-items-center mr-3">
                  <User size="14" className="mr-1 text-green-400" />
                  {item?.customer?.first_name} {item?.customer?.last_name}
                </p>
              )}

              {item?.installation_address?.address1 && (
                <p className="my-2  flex align-items-center">
                  <MapPin size="14" className="mr-1 text-pink-600" />
                  {item?.installation_address?.address1}
                </p>
              )}
              {item.city && (
                <p className="my-1 flex align-items-center">
                  <MapPin size="14" className="mr-1 text-pink-600" />
                  {`${item?.city}, ${item?.state_name}`}
                </p>
              )}
            </>
          </div>
        )}
        {/* Address ends */}

        {isFilterSectionOptionValue(searchParameter) && (
          <div className="text-xs border-top-none border-left-none border-right-none border-cyan-50 py-0">
            <div className="flex align-items-center">
              <FileText size="14" className="mr-1 text-blue-600" />
              <p className="my-0 pt-1">{item?.displayValue}</p>
            </div>
          </div>
        )}
      </>
    );
  };

  const itemTemplateCustom = (option: GlobalSearchOptions) => (
    <div className="flex align-items-center gap-1">
      <span className="icon-container bg-span border-round-sm px-1">
        <i className={`text-sm ${option?.icon} ${option?.className || ''}`} />
      </span>
      <span className="text-sm text-900">{option?.label}</span>
    </div>
  );

  const valueTemplate = (option: GlobalSearchOptions) => {
    return (
      <div className="flex align-items-center gap-1">
        <span>{option?.label}</span>
      </div>
    );
  };

  const show = () => {
    return toast.current?.show({ severity: 'info', summary: NO_DATA_FOUND });
  };
  useEffect(() => {
    autoRef?.current?.show();
  }, [isHardRefresh]);
  const handleSearch = async (
    redirectionMethod: string = '_self',
    searchValue: string | SearchValueType = value
  ) => {
    const validationResult = validateGlobalSearchInput(
      searchParameter,
      searchValue
    );
    if (validationResult.status) {
      if (searchValue) {
        let val = '';
        if (typeof searchValue === 'string') {
          val = searchValue;
        } else if (isFilterSectionOptionValue(searchParameter)) {
          val = searchValue?.displayValue || '';
        } else if (PROJECT_KEYS.includes(searchParameter)) {
          val = searchValue?.project_number || '';
        } else {
          val = searchValue?.customer_name || '';
        }
        const searchResultLength = val?.length;
        setSearchLength(searchResultLength);
        if (searchResultLength >= 3) {
          globalProjectSearch({
            searchValue:
              typeof searchValue === 'string'
                ? searchValue
                : String(searchValue),
            searchParameter,
            redirectionMethod,
            history,
            searchSuggession: undefined,
          });
        }

        setValue(val?.trim());

        if (
          typeof searchValue !== 'string' &&
          isFilterSectionOptionValue(searchParameter) &&
          searchValue.entityTypeId &&
          searchValue.id
        ) {
          const url = getEntityViewPath(
            searchValue.entityTypeId,
            searchValue.id
          );
          setVisible(false);
          redirectPage(url, redirectionMethod);
        } else if (
          typeof searchValue !== 'string' &&
          (searchValue.customer_id || searchValue.project_id)
        ) {
          const url = !['project_number', 'po_number', 'address'].includes(
            searchParameter
          )
            ? `/crm/view/customer/${searchValue.customer_id}`
            : `/project/view/${searchValue.project_id}`;

          redirectPage(
            `${url}?searchType=${searchParameter}&searchValue=${val}`,
            redirectionMethod
          );
        }
      }
    } else {
      toast?.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: validationResult.message,
        life: 3000,
      });
    }
  };

  const globalProjectSearch = async ({
    searchValue,
    searchParameter,
    history,
    searchSuggession,
    redirectionMethod = '_self',
  }: GlobalProjectSearchParams): Promise<void> => {
    clearToast();

    if (isFilterSectionOptionValue(searchParameter)) {
      if (!isNclcEnabled) {
        return;
      }

      const parsed = parseFilterSectionOptionValue(searchParameter);
      if (!parsed) {
        return;
      }

      setGlobalSearchLoader(true);
      const rows = await searchFormModuleReportData({
        entityTypeId: parsed.entityTypeId,
        keyName: parsed.keyName,
        searchValue: String(searchValue),
      });
      setGlobalSearchLoader(false);

      if (rows == null) {
        return;
      }

      if (!searchSuggession && rows.length > 0) {
        const firstRow = mapReportRowToSearchItem(
          rows[0],
          parsed.entityTypeId,
          parsed.keyName
        );
        setVisible(false);
        redirectPage(
          getEntityViewPath(firstRow.entityTypeId, firstRow.id),
          redirectionMethod
        );
        return;
      }

      if (rows.length > 0) {
        setOptions(
          rows.map(row =>
            mapReportRowToSearchItem(row, parsed.entityTypeId, parsed.keyName)
          )
        );
        setIsHardRefresh(prev => !prev);
      } else {
        show();
      }
      return;
    }

    const response = await searchProjects({
      searchValue,
      searchParameter,
      history,
      searchSuggession,
      setIsHardRefresh,
      setGlobalSearchLoader,
    });

    if (response?.length > 0) {
      setOptions(response);
    } else if (Array.isArray(response)) {
      show();
    }
  };

  const contextMenuItems = [
    {
      label: 'Open In New Tab',
      icon: 'pi pi-directions',
      command: () => {
        handleSearch('_blank');
      },
    },
  ];
  const getFilteredSearchOptions = (
    allOptions: GlobalSearchOptions[] = [],
    searchOptions?: (string | number)[],
    defaultOption?: string
  ) => {
    if (!Array.isArray(searchOptions) || searchOptions.length === 0) {
      return isNclcEnabled
        ? allOptions
        : allOptions.filter(
            option => !isFilterSectionOptionValue(String(option?.value))
          );
    }

    const selectedValues = searchOptions
      .map(value => String(value))
      .filter(value => isNclcEnabled || !isFilterSectionOptionValue(value));

    return (
      allOptions
        ?.filter(option => {
          const optionValue = String(option?.value);
          return (
            selectedValues.includes(optionValue) &&
            (isNclcEnabled || !isFilterSectionOptionValue(optionValue))
          );
        })
        ?.sort((a, b) => {
          if (String(a?.value) === String(defaultOption)) return -1;
          if (String(b?.value) === String(defaultOption)) return 1;
          return 0;
        }) ?? []
    );
  };

  return (
    <div
      className={`w-full align-items-center flex max-w-30rem ${styles.projectSearchbar}`}
    >
      <Toast className="search-notification" ref={toast} position="top-right" />
      {checkPermission(permissions?.globalSearch?.menu) && (
        <div className="p-inputgroup shadow-1 relative flex align-items-center border-round-xl bg-white px-2">
          <Dropdown
            dropdownIcon="pi pi-chevron-down text-xs"
            value={searchParameter}
            onChange={event => {
              const selectedValue = event?.target?.value;
              const enableSuggestion =
                SUGGESTION_ENABLED_KEYS.includes(selectedValue);

              clearToast();
              setSearchParameter(selectedValue);
              setOptions([]);

              if (!tenantSearchConfig.clear_global_search_text) {
                setValue('');
              }

              const suggestionValue = enableSuggestion ? 1 : searchSuggession;
              setSearchSuggession(suggestionValue);

              localStorage.setItem(
                'search-suggession',
                String(
                  FORCE_SUGGESTION_KEYS.includes(selectedValue)
                    ? 1
                    : searchSuggession
                )
              );

              setSelectedCityName(null);
              setAddressSearch('');
            }}
            options={
              tenantSearchConfig.search_options?.length > 0
                ? getFilteredSearchOptions(
                    searchTypeOptions,
                    tenantSearchConfig.search_options,
                    tenantSearchConfig.default_search_option
                  )
                : searchTypeOptions
            }
            itemTemplate={itemTemplateCustom}
            valueTemplate={valueTemplate}
            optionLabel="label"
            optionValue="value"
            placeholder={
              searchTypeOptions.find(item => item.value === searchParameter)
                ?.label ?? ''
            }
            className="absolute z-5 w-6rem border-none border-right-1 border-200 border-round-left-xl align-items-center height-40 line-height-1"
            pt={{
              input: {
                className: 'pl-0 pr-2 w-4rem sm:w-5rem border-y-none text-900',
              },
              root: {
                className: 'shadow-none',
              },
              trigger: {
                className: `w-2rem ${styles.searchOptionArrow}`,
              },
              wrapper: {
                className: 'max-h-20rem w-16rem',
              },
              list: {
                className: `${styles.headerSearchOption}`,
              },
              item: { className: 'outline-none' },
            }}
          />

          <div className="w-full">
            <i className="pi pi-search pl-8 ml-4 text-sm text-400 hidden xl:inline-flex"></i>
            <AutoComplete
              appendTo="self"
              ref={autoRef}
              className="border-noround w-12 lg:w-9 xl:w-8"
              inputClassName="border-round-xl p-inputtext-sm border-none focus:shadow-none border-round-md h-3rem pl-8 xl:pl-0 ml-4 xl:ml-2"
              suggestions={searchSuggession ? options : undefined}
              completeMethod={() => {}}
              value={value}
              forceSelection
              loadingIcon
              onBlur={() => setValue(value)}
              pt={{
                list: { className: 'p-0' },
                panel: {
                  className:
                    'w-12 w-19rem md:w-25rem xl:w-25rem xl:-ml-4 md:ml-1 left-0 project-search-option',
                },
                item: {
                  className: `${styles.searchResultLayout} border-1 border-round-sm px-1 py-2 mb-1 m-1 cursor-pointer`,
                },
              }}
              onChange={async e => {
                const rawValue = e?.value;
                const query = rawValue;
                const searchResultLength = rawValue?.length ?? 0;

                const validationResult = validateGlobalSearchInput(
                  searchParameter,
                  query
                );

                if (validationResult?.status) {
                  if (searchSuggession) {
                    setSearchLength(searchResultLength);

                    if (searchResultLength === 0) {
                      setOptions([]);
                      setSelectedCityName(null);
                      setIsHardRefresh(false);
                    }

                    /* ---------------- Address Search ---------------- */
                    if (searchParameter === 'address') {
                      if (searchResultLength >= 3 || rawValue?.city) {
                        if (selectedCityName || rawValue?.city) {
                          const searchValue = rawValue?.city
                            ? `${rawValue.city} ${rawValue?.state_name}`
                            : query;

                          globalProjectSearch({
                            searchValue,
                            searchParameter,
                            redirectionMethod: '_self',
                            history,
                            searchSuggession,
                          });
                        } else {
                          const filtered = citiesData?.filter(item =>
                            Object.values(item).some(value =>
                              String(value || '')
                                .toLowerCase()
                                .includes(String(query || '').toLowerCase())
                            )
                          );

                          if (!filtered?.length) {
                            if (searchResultLength >= 3) {
                              globalProjectSearch({
                                searchValue: query,
                                searchParameter,
                                redirectionMethod: '_self',
                                history,
                                searchSuggession,
                              });
                            }
                          } else {
                            setOptions(filtered);
                            if (isHardRefresh) setIsHardRefresh(Math.random());
                          }
                        }
                      }
                    } else if (searchResultLength >= 3) {
                      /* ---------------- Non-Address Search ---------------- */
                      globalProjectSearch({
                        searchValue: rawValue?.trim(),
                        searchParameter,
                        redirectionMethod: '_self',
                        history,
                        searchSuggession,
                      });
                    }

                    /* ---------------- Value Mapping ---------------- */
                    let val = rawValue;

                    if (typeof rawValue !== 'string') {
                      if (isFilterSectionOptionValue(searchParameter)) {
                        val = rawValue?.displayValue;
                      } else if (searchParameter === 'project_number') {
                        val = rawValue?.project_number;
                      } else if (searchParameter === 'po_number') {
                        val = rawValue?.po_number;
                      } else if (searchParameter === 'address') {
                        val = rawValue?.city
                          ? `${rawValue.city} ${rawValue?.state_name}`
                          : addressSearch;
                        setSelectedCityName(rawValue?.city);
                      } else if (searchParameter === 'client') {
                        val = rawValue?.customer_name;
                      } else if (searchParameter === 'phone') {
                        val = rawValue?.mobile_phone;
                      }
                    }

                    setAddressSearch(val);
                    setValue(val);

                    /* ---------------- Redirect ---------------- */
                    if (
                      isFilterSectionOptionValue(searchParameter) &&
                      rawValue?.entityTypeId &&
                      rawValue?.id
                    ) {
                      setVisible(false);
                      history?.push(
                        getEntityViewPath(rawValue.entityTypeId, rawValue.id)
                      );
                    } else if (rawValue?.customer_id || rawValue?.project_id) {
                      const url = !PROJECT_AND_ADDRESS_KEYS.includes(
                        searchParameter
                      )
                        ? `/crm/view/customer/${rawValue.customer_id}`
                        : `/project/view/${rawValue.project_id}/#tab=scheduleInfo`;
                      setVisible(false);
                      history?.push(
                        `${url}?searchType=${searchParameter}&searchValue=${val}`
                      );
                    }
                  } else {
                    /* ---------------- No Suggestion Mode ---------------- */
                    setValue(e?.target?.value);

                    if (searchResultLength >= 3) {
                      globalProjectSearch({
                        searchValue: rawValue?.trim(),
                        searchParameter,
                        redirectionMethod: '_self',
                        history,
                        searchSuggession,
                      });
                    }
                  }
                } else if (query) {
                  /* ---------------- Validation Error ---------------- */
                  setValue(e?.target?.value);
                  toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: validationResult.message,
                    life: 3000,
                  });
                } else {
                  /* ---------------- Empty Input ---------------- */
                  setValue('');
                }
              }}
              delay={500}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (!searchSuggession && e.key === 'Enter') {
                  handleSearch('_self', e.currentTarget?.value?.trim());
                }
              }}
              placeholder="Search"
              itemTemplate={itemTemplate}
            />
          </div>
          <div className="absolute right-0 flex align-items-center mr-2">
            {searchSuggession === 0 && searchParameter !== 'address' ? (
              <>
                <ContextMenu
                  model={contextMenuItems}
                  ref={cm}
                  breakpoint="767px"
                  pt={{
                    icon: {
                      className: 'text-lg',
                    },
                  }}
                />
                <Button
                  icon={
                    globalSearchLoader
                      ? 'pi pi-spin pi-spinner text-white'
                      : 'pi pi-search'
                  }
                  onClick={() => handleSearch()}
                  text
                  className="border-noround text-white bg-transparent focus:shadow-none"
                  onContextMenu={e => cm?.current?.show(e)}
                  disabled={globalSearchLoader === 1 ? true : false}
                />
              </>
            ) : null}

            {PROJECT_KEYS.includes(searchParameter) ||
            isFilterSectionOptionValue(searchParameter) ? (
              <div className="inline-flex align-items-center">
                <InputSwitch
                  checked={searchSuggession ? true : false}
                  onChange={e => onSearchSuggessionHandler(e.value)}
                  pt={{
                    slider: {
                      className: 'shadow-none',
                    },
                  }}
                  tooltipOptions={{
                    position: 'left',
                  }}
                  className="text-xs"
                  tooltip="Suggestions"
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSearchBar;
